import { hasSelectedEntity, selectedEntityIds } from '../../../shared/utils/entitySelection';
import { getStoredSessionToken } from '../../../app/services/session.service';
import {
  listInvoicesResult,
  listPaymentsWithClientIdsResult,
} from '@/modules/accounting/services/accounting.service';
import {
  listAssignmentsResult,
  listCompletedDealsResult,
  listInProgressDealsResult,
  listProposalsResult,
} from '@/modules/deals/services/deals.service';
import { listDocumentsResult } from '@/modules/documents/services/documents.service';
import { listStaffResult } from '@/modules/staff/services';
import {
  getCanonicalClientContacts,
  resolvePrimaryClientContact,
} from '../utils/clientContacts.helpers';

const CLIENTS_API_BASE_URL = (process.env.REACT_APP_CLIENTS_API_BASE_URL || '').replace(/\/$/, '');
const CLIENTS_API_PATH = '/api/clients';
const TENANT_DB_ERROR_CODES = new Set([
  'tenant_database_not_ready',
  'tenant_database_unavailable',
  'tenant_database_metadata_missing',
]);
const SESSION_REFRESH_REQUIRED_ERROR_CODES = new Set(['stale_session', 'session_refresh_required']);
const SESSION_EXPIRED_ERROR_CODES = new Set(['session_expired']);
const CLIENTS_TENANT_DB_RESULT_ERRORS = new Set([
  'CLIENTS_TENANT_DATABASE_NOT_READY',
  'CLIENTS_TENANT_DATABASE_UNAVAILABLE',
  'CLIENTS_TENANT_DATABASE_METADATA_MISSING',
]);

function buildMutationResult({ ok, data = null, error = null, message = '' }) {
  return {
    ok,
    data,
    error,
    message,
  };
}

function getClientsApiUrl(path = '') {
  return `${CLIENTS_API_BASE_URL}${path || CLIENTS_API_PATH}`;
}

function isAuthFailure(status) {
  return status === 401 || status === 403;
}

async function isModuleDisabledResponse(response) {
  if (response.status !== 403) return false;

  try {
    const payload = await response.clone().json();
    return payload?.error === 'module_disabled';
  } catch {
    return false;
  }
}

async function readErrorPayload(response) {
  try {
    return await response.clone().json();
  } catch {
    return null;
  }
}

function mapTenantDatabaseError(payload) {
  if (!TENANT_DB_ERROR_CODES.has(payload?.error)) {
    return null;
  }

  return {
    error:
      payload.error === 'tenant_database_not_ready'
        ? 'CLIENTS_TENANT_DATABASE_NOT_READY'
        : payload.error === 'tenant_database_metadata_missing'
          ? 'CLIENTS_TENANT_DATABASE_METADATA_MISSING'
          : 'CLIENTS_TENANT_DATABASE_UNAVAILABLE',
    message: payload?.message || 'Database tenant non disponibile per il modulo clienti.',
  };
}

function mapClientsAuthError(status, payload, fallbackMessage) {
  if (payload?.error === 'module_disabled') {
    return {
      error: 'CLIENTS_MODULE_DISABLED',
      message: 'Modulo non abilitato per questo workspace.',
    };
  }

  if (SESSION_REFRESH_REQUIRED_ERROR_CODES.has(payload?.error)) {
    return {
      error: 'CLIENTS_SESSION_REFRESH_REQUIRED',
      message: "Sessione non aggiornata. Effettua di nuovo l'accesso.",
    };
  }

  if (SESSION_EXPIRED_ERROR_CODES.has(payload?.error)) {
    return {
      error: 'CLIENTS_SESSION_EXPIRED',
      message: 'Sessione scaduta.',
    };
  }

  if (status === 403) {
    return {
      error: 'CLIENTS_PERMISSION_DENIED',
      message: payload?.message || 'Permessi insufficienti.',
    };
  }

  if (status === 401) {
    return {
      error: 'CLIENTS_AUTH_REQUIRED',
      message: payload?.message || fallbackMessage,
    };
  }

  return null;
}

function getClientsAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();

  if (!token) {
    return extraHeaders;
  }

  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
  };
}

function getClientsBearerToken() {
  return getStoredSessionToken();
}

function buildClientsAuthRequiredResult(message) {
  return buildMutationResult({
    ok: false,
    error: 'CLIENTS_AUTH_REQUIRED',
    message,
  });
}

async function performListClientsRequest({ includeArchived = false } = {}) {
  const query = includeArchived ? '?includeArchived=true' : '';
  const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}${query}`), {
    headers: getClientsAuthHeaders(),
  });

  if (await isModuleDisabledResponse(response)) {
    throw new Error('CLIENTS_MODULE_DISABLED');
  }

  const payload = await readErrorPayload(response);
  const tenantDatabaseError = mapTenantDatabaseError(payload);
  if (tenantDatabaseError) {
    const error = new Error(tenantDatabaseError.error);
    error.userMessage = tenantDatabaseError.message;
    throw error;
  }

  if (!response.ok) {
    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        payload,
        'Sessione non autorizzata per il catalogo clienti.'
      );
      const error = new Error(authError?.error || `CLIENTS_LIST_FAILED_${response.status}`);
      error.userMessage = authError?.message || 'Sessione non autorizzata per il catalogo clienti.';
      throw error;
    }

    throw new Error(`CLIENTS_LIST_FAILED_${response.status}`);
  }

  const successPayload = await response.json();
  if (!Array.isArray(successPayload)) {
    throw new Error('CLIENTS_LIST_INVALID_PAYLOAD');
  }

  return successPayload.map(mapApiClientToClient);
}

function normalizeOptional(value) {
  return value == null ? '' : String(value).trim();
}

function hasOwn(object, property) {
  return Object.prototype.hasOwnProperty.call(object ?? {}, property);
}

function hasStructuredContactField(payload = {}, field) {
  return (
    (hasOwn(payload, 'contattiPersonali') && (
      payload.contattiPersonali === null ||
      hasOwn(payload.contattiPersonali, field)
    )) ||
    (hasOwn(payload, 'contattiAziendali') && (
      payload.contattiAziendali === null ||
      hasOwn(payload.contattiAziendali, field)
    ))
  );
}

function appendPrimaryContact(request, payload, field) {
  if (hasStructuredContactField(payload, field)) {
    request[field] = normalizeOptional(
      resolvePrimaryClientContact(payload, field, {
        allowLegacyFallback: false,
      })
    );
    return;
  }

  if (hasOwn(payload, field)) {
    request[field] = normalizeOptional(payload[field]);
  }
}

function mapApiClientToClient(client) {
  const canonicalContacts = getCanonicalClientContacts(client);

  return {
    id: client?.id,
    name: client?.name || '',
    company: client?.company || '',
    tipoCliente: client?.tipoCliente || '',
    referredBy: client?.referredBy || '',
    businessName: client?.businessName || '',
    vatNumber: client?.vatNumber || '',
    sector: client?.sector || '',
    numeroREA: client?.numeroREA || '',
    codiceFiscale: client?.codiceFiscale || '',
    formaGiuridica: client?.formaGiuridica || '',
    indirizzoSedeLegale: client?.indirizzoSedeLegale || '',
    email: client?.email || '',
    phone: client?.phone || '',
    contattiPersonali: canonicalContacts.contattiPersonali,
    contattiAziendali: canonicalContacts.contattiAziendali,
    notes: client?.notes || '',
    ...(client?.customFields ? { customFields: client.customFields } : {}),
    archived: Boolean(client?.archived),
    documenti: [],
  };
}

function buildClientRequest(payload = {}) {
  const request = {
    name: normalizeOptional(payload.name),
    company: normalizeOptional(payload.company),
    tipoCliente: normalizeOptional(payload.tipoCliente),
    referredBy: normalizeOptional(payload.referredBy),
    businessName: normalizeOptional(payload.businessName),
    vatNumber: normalizeOptional(payload.vatNumber),
    sector: normalizeOptional(payload.sector),
    numeroREA: normalizeOptional(payload.numeroREA),
    codiceFiscale: normalizeOptional(payload.codiceFiscale),
    formaGiuridica: normalizeOptional(payload.formaGiuridica),
    indirizzoSedeLegale: normalizeOptional(payload.indirizzoSedeLegale),
  };

  appendPrimaryContact(request, payload, 'email');
  appendPrimaryContact(request, payload, 'phone');

  if (hasOwn(payload, 'contattiPersonali')) {
    request.contattiPersonali = payload.contattiPersonali === null
      ? null
      : buildContactRequest(payload.contattiPersonali);
  }
  if (hasOwn(payload, 'contattiAziendali')) {
    request.contattiAziendali = payload.contattiAziendali === null
      ? null
      : buildContactRequest(payload.contattiAziendali);
  }
  request.notes = normalizeOptional(payload.notes);
  if (hasOwn(payload, 'customFields')) {
    request.customFields = payload.customFields;
  }

  return request;
}

export async function listClientCustomFields() {
  const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/custom-fields`), { headers: getClientsAuthHeaders() });
  const payload = await readErrorPayload(response);
  if (!response.ok) throw new Error(payload?.message || 'Campi personalizzati non disponibili.');
  return Array.isArray(payload) ? payload : [];
}

export async function createClientCustomField(definition) {
  const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/custom-fields`), { method: 'POST', headers: getClientsAuthHeaders({ 'Content-Type': 'application/json' }), body: JSON.stringify(definition) });
  const payload = await response.json().catch(() => null);
  if (!response.ok) return buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_CREATE_FAILED', message: payload?.message || 'Impossibile salvare il campo.' });
  return buildMutationResult({ ok: true, data: payload });
}

export async function archiveClientCustomField(id) {
  const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/custom-fields/${id}`), { method: 'DELETE', headers: getClientsAuthHeaders() });
  const payload = await readErrorPayload(response);
  if (!response.ok) return buildMutationResult({ ok: false, error: 'CUSTOM_FIELD_ARCHIVE_FAILED', message: payload?.message || 'Impossibile archiviare il campo.' });
  return buildMutationResult({ ok: true });
}

function buildContactRequest(contact = {}) {
  const request = {};
  if (hasOwn(contact, 'email')) {
    request.email = contact.email === null ? null : normalizeOptional(contact.email);
  }
  if (hasOwn(contact, 'phone')) {
    request.phone = contact.phone === null ? null : normalizeOptional(contact.phone);
  }
  return request;
}

function normalizeRelationId(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value);
}

function filterByClientId(items = [], clientId) {
  const normalizedClientId = normalizeRelationId(clientId);
  if (!normalizedClientId) return [];

  return items.filter((item) => hasSelectedEntity(item, 'client', normalizedClientId));
}

function isAuthResult(result) {
  return typeof result?.error === 'string' && result.error.endsWith('_AUTH_REQUIRED');
}

function resolveSectionStatus(results, itemCount) {
  if (results.some(isAuthResult)) return 'auth_required';
  if (results.some((result) => !result?.ok)) return 'unavailable';
  return itemCount > 0 ? 'available' : 'empty';
}

function resolveOverallStatus(sections) {
  const statuses = sections.map((section) => section.status);
  if (statuses.every((status) => status === 'empty')) return 'empty';
  if (statuses.some((status) => status === 'auth_required' || status === 'unavailable')) {
    return 'partial';
  }
  return 'available';
}

function getDocumentsPayload(result) {
  return Array.isArray(result?.data?.documents) ? result.data.documents : [];
}

function buildSectionError(results) {
  const failed = results.find((result) => !result?.ok);
  return failed?.error || null;
}

export function buildClientFinancialsFromAccounting({ clientId, invoices = [], payments = [] }) {
  const normalizedClientId = normalizeRelationId(clientId);
  if (!normalizedClientId) {
    return {
      total: 0,
      received: 0,
      pending: 0,
    };
  }

  const clientInvoices = invoices.filter(
    (invoice) => hasSelectedEntity(invoice, 'client', normalizedClientId)
  );
  const invoiceIds = new Set(
    clientInvoices
      .map((invoice) => normalizeRelationId(invoice.id))
      .filter(Boolean)
  );
  const legacyInvoiceNumbers = new Set(
    clientInvoices
      .map((invoice) => (invoice.invoiceNumber ? String(invoice.invoiceNumber) : ''))
      .filter(Boolean)
  );

  const clientPayments = payments.filter((payment) => {
    const paymentClientIds = selectedEntityIds(payment, 'client');
    if (paymentClientIds.length) {
      return paymentClientIds.includes(normalizedClientId);
    }

    const paymentInvoiceId = normalizeRelationId(payment.invoiceId);
    if (paymentInvoiceId) {
      return invoiceIds.has(paymentInvoiceId);
    }

    return payment.invoiceNumber && legacyInvoiceNumbers.has(String(payment.invoiceNumber));
  });

  return {
    total: clientPayments.reduce((sum, payment) => sum + payment.amount, 0),
    received: clientPayments
      .filter((payment) => payment.status === 'incassato')
      .reduce((sum, payment) => sum + payment.amount, 0),
    pending: clientPayments
      .filter((payment) => payment.status === 'previsto' || payment.status === 'in_ritardo')
      .reduce((sum, payment) => sum + payment.amount, 0),
  };
}

export async function loadClientFinancialsResult(clientId) {
  if (!clientId) {
    return {
      ok: true,
      data: { total: 0, received: 0, pending: 0 },
      error: null,
    };
  }

  const [invoicesResult, paymentsResult] = await Promise.all([
    listInvoicesResult(),
    listPaymentsWithClientIdsResult(),
  ]);

  if (!invoicesResult.ok || !paymentsResult.ok) {
    return {
      ok: false,
      data: null,
      error: invoicesResult.error || paymentsResult.error || 'ACCOUNTING_CLIENT_FINANCIALS_UNAVAILABLE',
    };
  }

  return {
    ok: true,
    data: buildClientFinancialsFromAccounting({
      clientId,
      invoices: invoicesResult.data,
      payments: paymentsResult.data,
    }),
    error: null,
  };
}

export function buildClientActivityReadModel({
  clientId,
  proposalsResult,
  assignmentsResult,
  inProgressResult,
  completedResult,
  invoicesResult,
  paymentsResult,
  documentsResult,
}) {
  const proposals = proposalsResult?.ok ? filterByClientId(proposalsResult.data, clientId) : [];
  const safeAssignmentsResult = assignmentsResult || { ok: true, data: [] };
  const assignments = safeAssignmentsResult.ok ? filterByClientId(safeAssignmentsResult.data, clientId) : [];
  const inProgressDeals = inProgressResult?.ok ? filterByClientId(inProgressResult.data, clientId) : [];
  const completedDeals = completedResult?.ok ? filterByClientId(completedResult.data, clientId) : [];
  const invoices = invoicesResult?.ok ? filterByClientId(invoicesResult.data, clientId) : [];
  const payments = paymentsResult?.ok ? filterByClientId(paymentsResult.data, clientId) : [];
  const documents = documentsResult?.ok ? filterByClientId(getDocumentsPayload(documentsResult), clientId) : [];

  const dealsCount = proposals.length + inProgressDeals.length + completedDeals.length;
  const accountingCount = invoices.length + payments.length;
  const documentsCount = documents.length;

  const assignmentsSection = {
    status: resolveSectionStatus([safeAssignmentsResult], assignments.length),
    error: buildSectionError([safeAssignmentsResult]),
    assignments,
    counts: {
      total: assignments.length,
      pending: assignments.filter((assignment) => assignment.status === 'da_accettare').length,
      inProgress: assignments.filter((assignment) => assignment.status === 'in_corso').length,
      completed: assignments.filter((assignment) => assignment.status === 'completato').length,
    },
  };

  const deals = {
    status: resolveSectionStatus([proposalsResult, inProgressResult, completedResult], dealsCount),
    error: buildSectionError([proposalsResult, inProgressResult, completedResult]),
    proposals,
    inProgressDeals,
    completedDeals,
    counts: {
      proposals: proposals.length,
      inProgress: inProgressDeals.length,
      completed: completedDeals.length,
      total: dealsCount,
    },
  };

  const accounting = {
    status: resolveSectionStatus([invoicesResult, paymentsResult], accountingCount),
    error: buildSectionError([invoicesResult, paymentsResult]),
    invoices,
    payments,
    counts: {
      invoices: invoices.length,
      openInvoices: invoices.filter((invoice) => {
        const status = String(invoice?.status || '').toLowerCase();
        return status !== 'paid' && status !== 'pagata' && status !== 'saldata';
      }).length,
      payments: payments.length,
    },
  };

  const documentsSection = {
    status: resolveSectionStatus([documentsResult], documentsCount),
    error: buildSectionError([documentsResult]),
    documents,
    counts: {
      total: documents.length,
    },
  };

  return {
    clientId,
    status: resolveOverallStatus([assignmentsSection, deals, accounting, documentsSection]),
    assignments: assignmentsSection,
    deals,
    accounting,
    documents: documentsSection,
  };
}

export async function loadClientActivityResult(clientId) {
  if (!clientId) {
    return {
      ok: false,
      data: null,
      error: 'CLIENT_ACTIVITY_CLIENT_ID_REQUIRED',
    };
  }

  const [
    proposalsResult,
    assignmentsResult,
    inProgressResult,
    completedResult,
    invoicesResult,
    paymentsResult,
    documentsResult,
  ] = await Promise.all([
    listProposalsResult(),
    listAssignmentsResult(),
    listInProgressDealsResult(),
    listCompletedDealsResult(),
    listInvoicesResult(),
    listPaymentsWithClientIdsResult(),
    listDocumentsResult(),
  ]);

  return {
    ok: true,
    data: buildClientActivityReadModel({
      clientId,
      proposalsResult,
      assignmentsResult,
      inProgressResult,
      completedResult,
      invoicesResult,
      paymentsResult,
      documentsResult,
    }),
    error: null,
  };
}

export async function listReferralStaffResult() {
  return listStaffResult();
}

export async function listClients() {
  try {
    return await performListClientsRequest();
  } catch {
    return [];
  }
}

export async function listClientsResult(options = {}) {
  try {
    const clients = await performListClientsRequest(options);

    return {
      ok: true,
      data: clients,
      error: null,
    };
  } catch (error) {
    return {
      ok: false,
      data: [],
        error:
          error instanceof Error && error.message === 'CLIENTS_MODULE_DISABLED'
            ? 'CLIENTS_MODULE_DISABLED'
          : error instanceof Error && CLIENTS_TENANT_DB_RESULT_ERRORS.has(error.message)
              ? error.message
            : error instanceof Error &&
                [
                  'CLIENTS_AUTH_REQUIRED',
                  'CLIENTS_SESSION_REFRESH_REQUIRED',
                  'CLIENTS_SESSION_EXPIRED',
                  'CLIENTS_PERMISSION_DENIED',
                ].includes(error.message)
              ? error.message
            : error instanceof Error && /CLIENTS_LIST_FAILED_(401|403)$/.test(error.message)
              ? 'CLIENTS_AUTH_REQUIRED'
              : error instanceof Error
                ? error.message
                : 'CLIENTS_API_UNAVAILABLE',
        message:
          error instanceof Error && error.message === 'CLIENTS_MODULE_DISABLED'
            ? 'Modulo clienti non abilitato per questo workspace.'
            : error instanceof Error &&
                [
                  'CLIENTS_AUTH_REQUIRED',
                  'CLIENTS_SESSION_REFRESH_REQUIRED',
                  'CLIENTS_SESSION_EXPIRED',
                  'CLIENTS_PERMISSION_DENIED',
                ].includes(error.message) &&
                typeof error.userMessage === 'string'
              ? error.userMessage
            : error instanceof Error && typeof error.userMessage === 'string'
              ? error.userMessage
            : '',
      };
  }
}

export async function createClient(payload) {
  try {
    const response = await fetch(getClientsApiUrl(CLIENTS_API_PATH), {
      method: 'POST',
      headers: getClientsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildClientRequest(payload)),
    });

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_VALIDATION_ERROR',
        message: 'Compila tutti i campi obbligatori del cliente.',
      });
    }

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (!response.ok) {
      if (isAuthFailure(response.status)) {
        const authError = mapClientsAuthError(
          response.status,
          tenantDbPayload,
          'Sessione non autorizzata per il backend clienti.'
        );
        return buildMutationResult({
          ok: false,
          error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
          message: authError?.message || 'Sessione non autorizzata per il backend clienti.',
        });
      }

      if (await isModuleDisabledResponse(response)) {
        return buildMutationResult({
          ok: false,
          error: 'CLIENTS_MODULE_DISABLED',
          message: 'Modulo clienti non abilitato per questo workspace.',
        });
      }

      throw new Error(`CLIENT_CREATE_FAILED_${response.status}`);
    }

    const payloadResponse = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        client: mapApiClientToClient(payloadResponse),
      },
      message: 'Cliente creato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Creazione cliente non completata.',
    });
  }
}

export async function updateClient(clientId, payload) {
  if (!clientId) {
    return buildMutationResult({
      ok: false,
      error: 'CLIENT_NOT_FOUND',
      message: 'Cliente non trovato.',
    });
  }

  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}`), {
      method: 'PUT',
      headers: getClientsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(buildClientRequest(payload)),
    });

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_NOT_FOUND',
        message: 'Cliente non trovato.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_VALIDATION_ERROR',
        message: 'Compila tutti i campi obbligatori del cliente.',
      });
    }

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENTS_MODULE_DISABLED',
        message: 'Modulo clienti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        tenantDbPayload,
        'Sessione non autorizzata per il backend clienti.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend clienti.',
      });
    }

    if (!response.ok) {
      throw new Error(`CLIENT_UPDATE_FAILED_${response.status}`);
    }

    const payloadResponse = await response.json();

    return buildMutationResult({
      ok: true,
      data: {
        client: mapApiClientToClient(payloadResponse),
      },
      message: 'Cliente aggiornato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Aggiornamento cliente non completato.',
    });
  }
}

export async function restoreClient(clientId) {
  if (!clientId) {
    return buildMutationResult({
      ok: false,
      error: 'CLIENT_NOT_FOUND',
      message: 'Cliente non trovato.',
    });
  }

  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}/restore`), {
      method: 'POST',
      headers: getClientsAuthHeaders(),
    });

    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENTS_MODULE_DISABLED',
        message: 'Modulo clienti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        payload,
        'Sessione non autorizzata per il backend clienti.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend clienti.',
      });
    }

    if (!response.ok) {
      throw new Error(`CLIENT_RESTORE_FAILED_${response.status}`);
    }

    const payloadResponse = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        client: mapApiClientToClient(payloadResponse),
      },
      message: 'Cliente riattivato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Riattivazione cliente non completata.',
    });
  }
}

export async function archiveClient(clientId) {
  if (!clientId) {
    return buildMutationResult({
      ok: false,
      error: 'CLIENT_NOT_FOUND',
      message: 'Cliente non trovato.',
    });
  }

  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}/archive`), {
      method: 'POST',
      headers: getClientsAuthHeaders(),
    });

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_NOT_FOUND',
        message: 'Cliente non trovato.',
      });
    }

    const tenantDbPayload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(tenantDbPayload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENTS_MODULE_DISABLED',
        message: 'Modulo clienti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        tenantDbPayload,
        'Sessione non autorizzata per il backend clienti.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per il backend clienti.',
      });
    }

    if (!response.ok) {
      throw new Error(`CLIENT_ARCHIVE_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      data: {
        clientId,
      },
      message: 'Cliente archiviato con successo.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Archiviazione cliente non completata.',
    });
  }
}

export async function deleteClientPermanently(clientId) {
  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}`), { method: 'DELETE', headers: getClientsAuthHeaders() });
    const payload = await readErrorPayload(response);
    if (response.status === 409) return buildMutationResult({ ok: false, error: 'CLIENT_DELETE_CONFLICT', message: payload?.message || 'Il cliente è collegato ad altri dati e può restare disattivato.' });
    if (!response.ok) return buildMutationResult({ ok: false, error: `CLIENT_DELETE_FAILED_${response.status}`, message: 'Eliminazione definitiva non consentita.' });
    return buildMutationResult({ ok: true, message: 'Cliente eliminato definitivamente.' });
  } catch {
    return buildMutationResult({ ok: false, error: 'CLIENTS_API_UNAVAILABLE', message: 'Backend clienti non disponibile.' });
  }
}

function mapApiClientNoteToNote(note) {
  return {
    id: note?.id || '',
    clientId: note?.clientId ?? '',
    parentId: note?.parentId ?? null,
    authorUserId: note?.authorUserId || '',
    linkedWorkspaceUserId: note?.linkedWorkspaceUserId || '',
    authorFullName: note?.authorFullName || note?.authorDisplayName || '',
    authorName: note?.authorName || note?.authorUserId || 'Utente',
    authorEmail: note?.authorEmail || note?.email || '',
    body: note?.body || '',
    createdAt: note?.createdAt || '',
  };
}

export async function getClientNotes(clientId) {
  if (!getClientsBearerToken()) {
    return buildClientsAuthRequiredResult('Sessione non autorizzata per le note cliente.');
  }

  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}/notes`), {
      headers: getClientsAuthHeaders(),
    });

    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENTS_MODULE_DISABLED',
        message: 'Modulo clienti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        payload,
        'Sessione non autorizzata per le note cliente.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per le note cliente.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_NOT_FOUND',
        message: 'Cliente non trovato.',
      });
    }

    if (!response.ok) {
      throw new Error(`CLIENT_NOTES_LIST_FAILED_${response.status}`);
    }

    const notesPayload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        notes: Array.isArray(notesPayload) ? notesPayload.map(mapApiClientNoteToNote) : [],
      },
      message: '',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Note non caricate.',
    });
  }
}

export async function createClientNote(clientId, body, parentId = null) {
  const trimmedBody = String(body || '').trim();
  if (!trimmedBody) {
    return buildMutationResult({
      ok: false,
      error: 'CLIENT_NOTE_EMPTY',
      message: 'Inserisci una nota prima di salvare.',
    });
  }

  if (!getClientsBearerToken()) {
    return buildClientsAuthRequiredResult('Sessione non autorizzata per le note cliente.');
  }

  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}/notes`), {
      method: 'POST',
      headers: getClientsAuthHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        body: trimmedBody,
        ...(parentId !== null && parentId !== undefined ? { parentId } : {}),
      }),
    });

    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENTS_MODULE_DISABLED',
        message: 'Modulo clienti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        payload,
        'Sessione non autorizzata per le note cliente.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per le note cliente.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_NOT_FOUND',
        message: 'Cliente non trovato.',
      });
    }

    if (response.status === 400) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_NOTE_INVALID',
        message: 'Inserisci una nota valida.',
      });
    }

    if (!response.ok) {
      throw new Error(`CLIENT_NOTE_CREATE_FAILED_${response.status}`);
    }

    const notePayload = await response.json();
    return buildMutationResult({
      ok: true,
      data: {
        note: mapApiClientNoteToNote(notePayload),
      },
      message: 'Nota aggiunta.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Nota non salvata.',
    });
  }
}

export async function deleteClientNote(clientId, noteId) {
  if (!clientId || !noteId) {
    return buildMutationResult({
      ok: false,
      error: 'CLIENT_NOTE_DELETE_INVALID',
      message: 'Nota cliente non valida.',
    });
  }

  if (!getClientsBearerToken()) {
    return buildClientsAuthRequiredResult('Sessione non autorizzata per le note cliente.');
  }

  try {
    const response = await fetch(getClientsApiUrl(`${CLIENTS_API_PATH}/${clientId}/notes/${noteId}`), {
      method: 'DELETE',
      headers: getClientsAuthHeaders(),
    });

    const payload = await readErrorPayload(response);
    const tenantDbError = mapTenantDatabaseError(payload);
    if (tenantDbError) {
      return buildMutationResult({
        ok: false,
        error: tenantDbError.error,
        message: tenantDbError.message,
      });
    }

    if (await isModuleDisabledResponse(response)) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENTS_MODULE_DISABLED',
        message: 'Modulo clienti non abilitato per questo workspace.',
      });
    }

    if (isAuthFailure(response.status)) {
      const authError = mapClientsAuthError(
        response.status,
        payload,
        'Sessione non autorizzata per le note cliente.'
      );
      return buildMutationResult({
        ok: false,
        error: authError?.error || 'CLIENTS_AUTH_REQUIRED',
        message: authError?.message || 'Sessione non autorizzata per le note cliente.',
      });
    }

    if (response.status === 404) {
      return buildMutationResult({
        ok: false,
        error: 'CLIENT_NOTE_NOT_FOUND',
        message: 'Nota cliente non trovata.',
      });
    }

    if (!response.ok) {
      throw new Error(`CLIENT_NOTE_DELETE_FAILED_${response.status}`);
    }

    return buildMutationResult({
      ok: true,
      data: {},
      message: 'Nota eliminata.',
    });
  } catch {
    return buildMutationResult({
      ok: false,
      error: 'CLIENTS_API_UNAVAILABLE',
      message: 'Backend clienti non disponibile. Nota non eliminata.',
    });
  }
}
